import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Users,
  CheckCircle,
  XCircle,
  Ban,
  Edit3,
  Search,
  ArrowUpDown,
  TrendingUp,
  ShoppingCart,
  DollarSign,
  BarChart3,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  XAxis,
} from 'recharts';
import {
  useTikTokFetch,
  Creator,
  formatCurrency,
  formatNumber,
} from '../hooks/useTikTokFetch';
import '../styles/pages.css';

const STATUS_OPTIONS = ['all', 'pending', 'approved', 'rejected', 'suspended'] as const;

type SortField = 'date' | 'commission' | 'revenue' | 'orders';
const SORT_OPTIONS: { value: SortField; label: string }[] = [
  { value: 'date', label: 'Date Joined' },
  { value: 'commission', label: 'Commission Rate' },
  { value: 'revenue', label: 'Total Sales' },
  { value: 'orders', label: 'Total Orders' },
];

interface CreatorPerformance {
  daily_sales: Array<{ date: string; amount: number }>;
  daily_orders: Array<{ date: string; count: number }>;
  commission_this_month: number;
}

function CreatorSparkline({ data, dataKey, color }: { data: Array<Record<string, unknown>>; dataKey: string; color: string }) {
  if (!data || data.length === 0) return <div className="sparkline-empty">No data</div>;
  return (
    <ResponsiveContainer width="100%" height={48}>
      <BarChart data={data} margin={{ top: 2, right: 0, bottom: 0, left: 0 }}>
        <RechartsTooltip
          contentStyle={{
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: '8px',
            fontSize: '0.7rem',
            padding: '4px 8px',
          }}
          labelStyle={{ color: 'var(--text-muted)', fontSize: '0.65rem' }}
          cursor={{ fill: 'rgba(255,255,255,0.04)' }}
        />
        <XAxis dataKey="date" hide />
        <Bar dataKey={dataKey} fill={color} radius={[2, 2, 0, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}

function StatusBadge({ status }: { status: string }) {
  const map: Record<string, string> = {
    approved: 'badge--success',
    pending: 'badge--warning',
    rejected: 'badge--danger',
    suspended: 'badge--danger',
  };
  return <span className={`badge ${map[status] || ''}`}>{status}</span>;
}

export default function Creators() {
  const { get, post, put, getShopId } = useTikTokFetch();
  const shopId = getShopId();
  const queryClient = useQueryClient();

  const [statusFilter, setStatusFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortAsc, setSortAsc] = useState(false);
  const [page, setPage] = useState(0);
  const [editingCommission, setEditingCommission] = useState<string | null>(null);
  const [newRate, setNewRate] = useState(0);
  const [expandedCreator, setExpandedCreator] = useState<string | null>(null);
  const [showSortMenu, setShowSortMenu] = useState(false);
  const limit = 20;

  const queryParams = new URLSearchParams({ limit: String(limit), offset: String(page * limit) });
  if (statusFilter !== 'all') queryParams.set('status', statusFilter);
  if (searchQuery.trim()) queryParams.set('search', searchQuery.trim());
  if (sortField !== 'date') queryParams.set('sort_by', sortField);
  if (sortAsc) queryParams.set('sort_asc', 'true');

  const { data, isLoading, refetch } = useQuery<{ creators: Creator[]; total: number }>({
    queryKey: ['creators', shopId, statusFilter, searchQuery, sortField, sortAsc, page],
    queryFn: () => get(`/api/shop/${shopId}/creators?${queryParams}`),
    enabled: !!shopId,
  });

  const creators = data?.creators ?? [];
  const total = data?.total ?? 0;

  // Fetch performance data for expanded creator
  const { data: perfData } = useQuery<CreatorPerformance>({
    queryKey: ['creator-performance', shopId, expandedCreator],
    queryFn: () => get(`/api/shop/${shopId}/creators/${expandedCreator}/performance`),
    enabled: !!shopId && !!expandedCreator,
  });

  const actionMutation = useMutation({
    mutationFn: ({ creatorId, action, reason }: { creatorId: string; action: string; reason?: string }) =>
      post(`/api/shop/${shopId}/creators/${creatorId}/action`, { action, reason }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['creators'] }),
  });

  const commissionMutation = useMutation({
    mutationFn: ({ creatorId, rate }: { creatorId: string; rate: number }) =>
      put(`/api/shop/${shopId}/creators/${creatorId}/commission`, { commission_rate: rate / 100 }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['creators'] });
      setEditingCommission(null);
    },
  });

  // Client-side search filter (supplements server-side)
  const filteredCreators = useMemo(() => {
    if (!searchQuery.trim()) return creators;
    const q = searchQuery.toLowerCase();
    return creators.filter(
      (c) =>
        (c.display_name || '').toLowerCase().includes(q) ||
        (c.tiktok_username || '').toLowerCase().includes(q)
    );
  }, [creators, searchQuery]);

  // Client-side sort
  const sortedCreators = useMemo(() => {
    const sorted = [...filteredCreators];
    sorted.sort((a, b) => {
      let diff = 0;
      switch (sortField) {
        case 'commission':
          diff = a.commission_rate - b.commission_rate;
          break;
        case 'revenue':
          diff = a.total_revenue - b.total_revenue;
          break;
        case 'orders':
          diff = a.total_orders - b.total_orders;
          break;
        case 'date':
        default:
          diff = 0; // Server already sorts by date
          break;
      }
      return sortAsc ? diff : -diff;
    });
    return sorted;
  }, [filteredCreators, sortField, sortAsc]);

  const totalPages = Math.ceil(total / limit);

  const toggleSort = (field: SortField) => {
    if (sortField === field) {
      setSortAsc(!sortAsc);
    } else {
      setSortField(field);
      setSortAsc(false);
    }
    setShowSortMenu(false);
    setPage(0);
  };

  // Summary stats
  const summaryStats = useMemo(() => {
    const activeCount = creators.filter((c) => c.status === 'approved').length;
    const pendingCount = creators.filter((c) => c.status === 'pending').length;
    const totalRev = creators.reduce((sum, c) => sum + c.total_revenue, 0);
    const totalOrd = creators.reduce((sum, c) => sum + c.total_orders, 0);
    return { activeCount, pendingCount, totalRev, totalOrd };
  }, [creators]);

  return (
    <div className="page-container">
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">Creators</h1>
        <div className="header-actions">
          <button className="btn btn--sm btn--secondary" onClick={() => refetch()} title="Refresh">
            <RefreshCw size={14} />
            Refresh
          </button>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="stat-grid" style={{ marginBottom: '1.25rem' }}>
        <div className="stat-card">
          <Users size={18} className="stat-icon" />
          <span className="stat-label">Total Creators</span>
          <span className="stat-value">{total}</span>
        </div>
        <div className="stat-card">
          <CheckCircle size={18} className="stat-icon" />
          <span className="stat-label">Active</span>
          <span className="stat-value">{summaryStats.activeCount}</span>
        </div>
        <div className="stat-card">
          <DollarSign size={18} className="stat-icon" />
          <span className="stat-label">Total Revenue</span>
          <span className="stat-value">{formatCurrency(summaryStats.totalRev)}</span>
        </div>
        <div className="stat-card">
          <ShoppingCart size={18} className="stat-icon" />
          <span className="stat-label">Total Orders</span>
          <span className="stat-value">{formatNumber(summaryStats.totalOrd)}</span>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="creators-toolbar">
        <div className="search-bar">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            className="input search-input"
            placeholder="Search by name or username..."
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(0);
            }}
          />
        </div>

        <div className="filter-row">
          {STATUS_OPTIONS.map((s) => (
            <button
              key={s}
              className={`filter-btn ${statusFilter === s ? 'filter-btn--active' : ''}`}
              onClick={() => {
                setStatusFilter(s);
                setPage(0);
              }}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
              {s === 'pending' && summaryStats.pendingCount > 0 && (
                <span className="filter-count">{summaryStats.pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        <div className="sort-dropdown-wrap">
          <button
            className="btn btn--sm btn--secondary"
            onClick={() => setShowSortMenu(!showSortMenu)}
          >
            <ArrowUpDown size={14} />
            Sort: {SORT_OPTIONS.find((o) => o.value === sortField)?.label}
            {sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
          </button>
          {showSortMenu && (
            <div className="sort-menu">
              {SORT_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  className={`sort-menu-item ${sortField === opt.value ? 'sort-menu-item--active' : ''}`}
                  onClick={() => toggleSort(opt.value)}
                >
                  {opt.label}
                  {sortField === opt.value && (sortAsc ? <ChevronUp size={12} /> : <ChevronDown size={12} />)}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Creator List */}
      {isLoading ? (
        <div className="loading-spinner" />
      ) : sortedCreators.length === 0 ? (
        <div className="empty-state">
          <Users size={48} className="text-muted" />
          <p>{searchQuery ? 'No creators match your search' : 'No creators found'}</p>
        </div>
      ) : (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
          <div className="creator-grid">
            <AnimatePresence>
              {sortedCreators.map((c) => (
                <motion.div
                  key={c.id}
                  className={`creator-card ${expandedCreator === c.id ? 'creator-card--expanded' : ''}`}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  layout
                >
                  {/* Card Header */}
                  <div className="creator-card-header">
                    <div className="user-cell">
                      {c.avatar_url ? (
                        <img src={c.avatar_url} alt="" className="avatar avatar--lg" />
                      ) : (
                        <div className="avatar avatar--lg avatar--placeholder">
                          {(c.display_name || c.tiktok_username || '?')[0]}
                        </div>
                      )}
                      <div className="creator-identity">
                        <span className="creator-name">{c.display_name || c.tiktok_username}</span>
                        {c.tiktok_username && (
                          <span className="text-muted text-sm">@{c.tiktok_username}</span>
                        )}
                        <div className="creator-meta">
                          <span className="text-sm text-muted">{formatNumber(c.follower_count)} followers</span>
                          <StatusBadge status={c.status} />
                        </div>
                      </div>
                    </div>

                    {/* Action Buttons */}
                    <div className="creator-actions">
                      {c.status === 'pending' && (
                        <>
                          <button
                            className="btn btn--sm btn--success"
                            onClick={() => actionMutation.mutate({ creatorId: c.id, action: 'approve' })}
                          >
                            <CheckCircle size={14} /> Approve
                          </button>
                          <button
                            className="btn btn--sm btn--danger"
                            onClick={() =>
                              actionMutation.mutate({
                                creatorId: c.id,
                                action: 'reject',
                                reason: 'Does not meet requirements',
                              })
                            }
                          >
                            <XCircle size={14} /> Reject
                          </button>
                        </>
                      )}
                      {c.status === 'approved' && (
                        <button
                          className="btn btn--sm btn--warning"
                          onClick={() =>
                            actionMutation.mutate({
                              creatorId: c.id,
                              action: 'suspend',
                              reason: 'Policy violation',
                            })
                          }
                        >
                          <Ban size={14} /> Suspend
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Metrics Row */}
                  <div className="creator-metrics">
                    <div className="creator-metric">
                      <span className="creator-metric-label">Revenue</span>
                      <span className="creator-metric-value">{formatCurrency(c.total_revenue)}</span>
                    </div>
                    <div className="creator-metric">
                      <span className="creator-metric-label">Orders</span>
                      <span className="creator-metric-value">{formatNumber(c.total_orders)}</span>
                    </div>
                    <div className="creator-metric">
                      <span className="creator-metric-label">Commission</span>
                      <span className="creator-metric-value">{formatCurrency(c.total_commission)}</span>
                    </div>
                    <div className="creator-metric">
                      <span className="creator-metric-label">Rate</span>
                      {editingCommission === c.id ? (
                        <div className="inline-edit">
                          <input
                            type="number"
                            min="1"
                            max="50"
                            value={newRate}
                            onChange={(e) => setNewRate(Number(e.target.value))}
                            className="input input--sm"
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                commissionMutation.mutate({ creatorId: c.id, rate: newRate });
                              } else if (e.key === 'Escape') {
                                setEditingCommission(null);
                              }
                            }}
                            autoFocus
                          />
                          <span className="text-sm text-muted">% (1-50)</span>
                          <button
                            className="btn btn--sm btn--primary"
                            onClick={() => commissionMutation.mutate({ creatorId: c.id, rate: newRate })}
                          >
                            Save
                          </button>
                          <button
                            className="btn btn--sm btn--ghost"
                            onClick={() => setEditingCommission(null)}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <button
                          className="editable-value"
                          onClick={() => {
                            setEditingCommission(c.id);
                            setNewRate(Math.round(c.commission_rate * 100));
                          }}
                          title="Click to edit"
                        >
                          {(c.commission_rate * 100).toFixed(0)}%
                          <Edit3 size={12} />
                        </button>
                      )}
                    </div>
                    <div className="creator-metric">
                      <span className="creator-metric-label">CVR</span>
                      <span className="creator-metric-value">{(c.conversion_rate * 100).toFixed(1)}%</span>
                    </div>
                  </div>

                  {/* Expand/Collapse */}
                  <button
                    className="creator-expand-btn"
                    onClick={() => setExpandedCreator(expandedCreator === c.id ? null : c.id)}
                  >
                    {expandedCreator === c.id ? (
                      <>
                        <ChevronUp size={14} /> Hide Charts
                      </>
                    ) : (
                      <>
                        <BarChart3 size={14} /> View Performance
                      </>
                    )}
                  </button>

                  {/* Expanded Performance Section */}
                  <AnimatePresence>
                    {expandedCreator === c.id && (
                      <motion.div
                        className="creator-performance"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                      >
                        <div className="perf-charts">
                          <div className="perf-chart-card">
                            <div className="perf-chart-header">
                              <TrendingUp size={14} />
                              <span>Sales (30 days)</span>
                            </div>
                            <CreatorSparkline
                              data={perfData?.daily_sales ?? []}
                              dataKey="amount"
                              color="var(--accent)"
                            />
                          </div>
                          <div className="perf-chart-card">
                            <div className="perf-chart-header">
                              <ShoppingCart size={14} />
                              <span>Orders (30 days)</span>
                            </div>
                            <CreatorSparkline
                              data={perfData?.daily_orders ?? []}
                              dataKey="count"
                              color="var(--primary)"
                            />
                          </div>
                          <div className="perf-chart-card">
                            <div className="perf-chart-header">
                              <DollarSign size={14} />
                              <span>Commission This Month</span>
                            </div>
                            <div className="perf-commission-value">
                              {formatCurrency(perfData?.commission_this_month ?? 0)}
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        </motion.div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="pagination">
          <button
            className="btn btn--sm"
            onClick={() => setPage(Math.max(0, page - 1))}
            disabled={page === 0}
          >
            Previous
          </button>
          <span className="text-secondary text-sm">
            Page {page + 1} of {totalPages}
          </span>
          <button
            className="btn btn--sm"
            onClick={() => setPage(page + 1)}
            disabled={page >= totalPages - 1}
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
}
